import { SelfServiceUserInfoDto } from '@ekt-group/general-purpose-api-interfaces';
import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { fetchFn, mutationFn } from '@/lib/react-query';
import { useUserInfoStore } from '@/store';

const authRoutes = {
  login: '/self-service/auth/login',
  logout: '/self-service/auth/logout',
  verify: '/self-service/auth/callback',
  me: '/self-service/auth/me',
  loginByTemporalToken: '/self-service/auth/login/toits',
};

const getUserFn = async (): Promise<SelfServiceUserInfoDto> => {
  return fetchFn(authRoutes.me);
};

const logOutFn = async () => {
  return fetchFn(authRoutes.logout);
};

interface LoginResponse {
  redirectUrl: string;
}

const logInFn = async (): Promise<LoginResponse> => {
  return fetchFn(authRoutes.login);
};

const logInByTemporalTokenFn = async (token: string): Promise<SelfServiceUserInfoDto> => {
  return mutationFn(authRoutes.loginByTemporalToken, {
    temporalToken: token,
  });
};

interface VerifyCallbackParams {
  code: string;
  state: string;
  iss: string;
}

const verifyCallbackFn = async (params: VerifyCallbackParams): Promise<SelfServiceUserInfoDto> => {
  return fetchFn(authRoutes.verify, {
    params,
  });
};

const configureAuth = () => {
  const useUser = (options?: Omit<UseQueryOptions<SelfServiceUserInfoDto>, 'queryKey' | 'queryFn'>) => {
    const { selectedAccount, setSelectedAccount, setHasSelectedAccountFirstTime } = useUserInfoStore();

    return useQuery({
      queryKey: ['user'],
      queryFn: async () => {
        try {
          const data = await getUserFn();
          if (data?.accounts?.length) {
            const foundSelectedAccount = data.accounts.find(({ code }) => code === selectedAccount?.code);
            if (!foundSelectedAccount) {
              setSelectedAccount(data.accounts[0]);
            }
            return data;
          }
          return data;
        } catch (error) {
          setHasSelectedAccountFirstTime(false);
        }
      },
      ...options,
    });
  };

  const useLogin = (options?: Omit<UseMutationOptions<LoginResponse>, 'mutationFn'>) =>
    useMutation({
      mutationFn: logInFn,
      ...options,
    });

  const useLogout = (options?: UseMutationOptions) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: logOutFn,
      onSuccess: (...rest) => {
        queryClient.setQueryData(['user'], undefined);
        options?.onSuccess?.(...rest);
      },
      ...options,
    });
  };

  const useVerifyCallback = (options?: Omit<UseMutationOptions<SelfServiceUserInfoDto, unknown, VerifyCallbackParams>, 'mutationFn'>) => {
    const queryClient = useQueryClient();
    const { setSelectedAccount } = useUserInfoStore();

    return useMutation({
      mutationFn: verifyCallbackFn,
      onSuccess: (data, ...rest) => {
        queryClient.setQueryData(['user'], data);
        setSelectedAccount(data.accounts[0]);
        options?.onSuccess?.(data, ...rest);
      },
      ...options,
    });
  };

  const useLoginByTemporalToken = (options?: Omit<UseMutationOptions<SelfServiceUserInfoDto, unknown, string>, 'mutationFn'>) => {
    const queryClient = useQueryClient();
    const { setSelectedAccount } = useUserInfoStore();

    return useMutation({
      mutationFn: logInByTemporalTokenFn,
      onSuccess: (data, ...rest) => {
        queryClient.setQueryData(['user'], data);
        setSelectedAccount(data.accounts[0]);
        options?.onSuccess?.(data, ...rest);
      },
      ...options,
    });
  };

  return {
    useUser,
    useLogin,
    useLogout,
    useVerifyCallback,
    useLoginByTemporalToken,
  };
};

export const { useUser, useLogin, useLogout, useVerifyCallback, useLoginByTemporalToken } = configureAuth();
